import React, {useState} from 'react';
import {Modal, Pressable, View, StyleSheet} from 'react-native';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import ErrorBanner from '@/Components/ErrorBanner';
import {setLocalStorage, removeLocalStorage} from '@/Utils/CommonUtils';
import {ScreenName} from '@/navigators/StackConstants';
import {useCreateProfileChallengeApi} from '@/Hooks/PlayerProfileApi/useCreateProfileChallengeApi';

const TIME_CONTROLS = ['1+0', '3+0', '3+2', '5+0', '10+0'];

const challengeErrorMessage = (error: any) => {
  const code = error?.response?.data?.error;
  if (code === 'blocked') {
    return error?.response?.data?.message || 'This player has Do Not Disturb on.';
  }
  if (code === 'duplicate_pending') {
    return 'You already have a challenge pending with this player.';
  }
  if (code === 'self_challenge') {
    return "You can't challenge yourself.";
  }
  return 'Something went wrong sending this challenge. Please try again.';
};

type Props = {
  visible: boolean;
  onClose: () => void;
  username: string;
  opponentUserId: number;
  opponentName: string;
  navigation: any;
};

// Challenge modal (Player Profile Milestone 3): time control + rated toggle,
// "Send challenge" persists a ProfileChallenge (reload-safe `challenge_pending`,
// see chess repo's player_profile/services/challenge.py) AND fires the same
// client-side WS handshake League/club challenges already use --
// `club_challenge_role`/`club_opp_id`/`club_opp_name`/`club_challenge_tc` keys
// via localStorage, then navigate into PlayChess as "creator" (this client
// plays White; the recipient's app-wide ChallengeComponent auto-accepts if
// they're online -- see src/Screens/PlayChess/index.js's club-challenge
// handoff, which this mirrors verbatim rather than inventing new keys).
export default function ChallengeModal({
  visible,
  onClose,
  username,
  opponentUserId,
  opponentName,
  navigation,
}: Props) {
  const [timeControl, setTimeControl] = useState('5+0');
  const [rated, setRated] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const createChallenge = useCreateProfileChallengeApi(username);

  const onSend = async () => {
    setError(null);
    try {
      const res = await createChallenge.mutateAsync({time_control: timeControl, rated});
      // Not a club challenge -- clear this key so PlayChess's club-challenge
      // handoff effect (which also checks `club_challenge_id`) doesn't
      // confuse this for a stale club challenge from a previous session.
      await removeLocalStorage('club_challenge_id');
      await setLocalStorage('club_opp_id', String(opponentUserId));
      await setLocalStorage('club_opp_name', String(opponentName));
      await setLocalStorage('club_challenge_tc', res.time_control);
      await setLocalStorage('club_challenge_role', 'creator');
      onClose();
      navigation.navigate(ScreenName.PlayChess);
    } catch (e) {
      setError(challengeErrorMessage(e));
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable testID="challenge-backdrop" style={styles.backdrop} onPress={onClose}>
        <Pressable style={styles.card} onPress={() => {}}>
          <View style={styles.headerRow}>
            <CCText fontWeight="semibold" style={styles.title}>
              Challenge {opponentName}
            </CCText>
            <Pressable testID="challenge-close" onPress={onClose}>
              <CCText style={styles.closeX}>×</CCText>
            </Pressable>
          </View>
          <CCText style={styles.subtitle}>Pick a time control and send.</CCText>

          {error ? (
            <ErrorBanner testID="challenge-error" message={error} style={{marginTop: 12}} />
          ) : null}

          <CCText style={styles.sectionLabel}>Time control</CCText>
          <View style={styles.tcRow}>
            {TIME_CONTROLS.map(tc => (
              <Pressable
                key={tc}
                testID={`challenge-tc-${tc}`}
                onPress={() => setTimeControl(tc)}
                style={[styles.tcChip, timeControl === tc && styles.tcChipActive]}>
                <CCText
                  style={[styles.tcChipText, timeControl === tc && styles.tcChipTextActive]}>
                  {tc}
                </CCText>
              </Pressable>
            ))}
          </View>

          <Pressable
            testID="challenge-rated-toggle"
            style={styles.ratedRow}
            onPress={() => setRated(r => !r)}>
            <CCText style={styles.ratedLabel}>Rated game</CCText>
            <View style={[styles.switchTrack, rated && styles.switchTrackOn]}>
              <View style={[styles.switchThumb, rated && styles.switchThumbOn]} />
            </View>
          </Pressable>

          <View style={styles.actionsRow}>
            <CCButton
              testID="challenge-send"
              // CCButton's `style` only reaches an inner View, not the
              // outer Pressable that actually participates in this row's
              // flex layout -- `flex: 1` has to go on `parentStyle`, or a
              // long opponent name pushes the button past the card's edge
              // (see CCButton.tsx: `style` -> getContainerStyle, an inner
              // child; `parentStyle` -> the Pressable itself).
              parentStyle={styles.sendBtn}
              onPress={onSend}
              disabled={createChallenge.isLoading}>
              {createChallenge.isLoading
                ? 'Sending…'
                : `Challenge ${opponentName}`}
            </CCButton>
            <CCButton
              type="outline"
              parentStyle={styles.cancelBtn}
              onPress={onClose}>
              Cancel
            </CCButton>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 22,
    padding: 22,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 8},
    shadowOpacity: 0.3,
    shadowRadius: 24,
    elevation: 8,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {fontSize: 18, color: '#262322'},
  subtitle: {fontSize: 12.5, color: '#888280', marginTop: 4},
  closeX: {fontSize: 22, color: '#4D3F37'},
  sectionLabel: {fontSize: 12.5, color: '#6F655F', marginTop: 14, marginBottom: 8},
  tcRow: {flexDirection: 'row', flexWrap: 'wrap', gap: 8},
  tcChip: {
    paddingVertical: 7,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.2)',
    backgroundColor: '#FAF6EB',
  },
  tcChipActive: {backgroundColor: '#3DAB9E', borderColor: '#3DAB9E'},
  tcChipText: {fontSize: 13, color: '#4D3F37'},
  tcChipTextActive: {color: '#fff', fontWeight: '600'},
  ratedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 18,
    backgroundColor: '#FAF6EB',
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  ratedLabel: {fontSize: 13, fontWeight: '600', color: '#4D3F37'},
  switchTrack: {
    width: 40,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#D9D5CF',
    padding: 2,
    justifyContent: 'center',
  },
  switchTrackOn: {backgroundColor: '#3DAB9E'},
  switchThumb: {
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: '#fff',
  },
  switchThumbOn: {alignSelf: 'flex-end'},
  actionsRow: {flexDirection: 'row', gap: 10, marginTop: 20},
  sendBtn: {flex: 1},
  cancelBtn: {},
});
